/**
 * Gerahmte Verschlüsselung für Zone-A-Anhänge — das Gegenstück zu ADR-011.
 *
 * ## Warum es das braucht
 *
 * ADR-011 beschreibt, wie der **Server** Zone-B-Blobs auf der Platte ablegt
 * (`core/blobstore.ts`, Format `FHB1`). Für Zone A steht dort ein einziger
 * Satz: „kommt bereits verschlüsselt an und wird unverändert abgelegt". Wer
 * das verschlüsselt, war damit nie gebaut — der Chat konnte deshalb bis heute
 * keinen Anhang, obwohl Tabelle, Endpunkt und Formatliste im Server seit
 * Phase 6 stehen und auf ihn warten.
 *
 * ## Das Format
 *
 * ```
 * "FHA1" | version(1) | dateiId(16) | [ len(4) | ciphertext | tag(16) ] …
 * ```
 *
 * - Nutzdaten je Rahmen: **64 KiB**, wie beim Server
 * - Nonce: der Rahmenindex, 12 Byte
 * - **AAD: `dateiId | index(4) | 1 wenn letzter Rahmen`**
 *
 * Drei Eigenschaften, alle drei Absicht — dieselben wie in ADR-011:
 *
 * 1. **Rahmen sind an ihre Position gebunden.** Vertauschen fällt auf — schon
 *    über den Nonce, der der Index ist; der Index im AAD ist das zweite
 *    Schloss. Gegengeprüft in `rahmen.test.ts`: Nimmt man ihn aus dem AAD,
 *    bleibt der Test grün, und zwar zu Recht.
 * 2. **Der letzte Rahmen ist als letzter markiert.** Abschneiden fällt auf.
 *    Ohne diese Markierung könnte jemand die zweite Hälfte eines Videos
 *    entfernen, und das Ergebnis wäre eine gültig entschlüsselbare, kürzere
 *    Datei — eine Manipulation, die wie ein Übertragungsfehler aussieht.
 * 3. **Rahmen sind an ihre Datei gebunden.** Die zufällige `dateiId` steht im
 *    Kopf und in jedem AAD; ein Rahmen aus einer anderen Datei passt nicht,
 *    selbst wenn er mit demselben Schlüssel entstanden wäre.
 *
 * ## Der Schlüssel wird nicht hereingereicht
 *
 * `verschluesseln` **erzeugt** ihn und gibt ihn zurück. Das ist kein
 * Komfortverzicht, sondern die Absicherung gegen den einen Fehler, der dieses
 * Format kaputt macht: Der Nonce ist der Rahmenindex, also beginnt jede Datei
 * wieder bei 0. Zwei Dateien mit demselben Schlüssel hätten für Rahmen 0
 * denselben Nonce — bei AES-GCM ist das der Totalschaden, nicht bloß eine
 * Schwäche. Wer den Schlüssel nicht hineingeben kann, kann ihn auch nicht
 * versehentlich zweimal verwenden.
 *
 * Für einen Anhang gibt es deshalb **einen** Zufallswert, aus dem per HKDF
 * drei getrennte Schlüssel entstehen: Datei, Vorschaubild, Metadaten.
 */

/** Nutzdaten je Rahmen. Gleich dem Serverformat — ein Wert, eine Begründung. */
export const RAHMEN_BYTES = 64 * 1024

const MAGIC = new TextEncoder().encode('FHA1')
const VERSION = 1
const DATEI_ID_BYTES = 16
const TAG_BYTES = 16
const KOPF_BYTES = MAGIC.length + 1 + DATEI_ID_BYTES
const LAENGE_BYTES = 4

/** Der Inhalt war nicht der, der er sein sollte. */
export class RahmenKaputt extends Error {
  constructor(grund: string) {
    super(grund)
    this.name = 'RahmenKaputt'
  }
}

export type Zweck = 'daten' | 'vorschau' | 'meta'

/** Ein neuer Anhangsschlüssel. Aus ihm leiten sich alle drei Teilschlüssel ab. */
export function neuerAnhangSchluessel(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(32))
}

/**
 * Teilschlüssel für einen Zweck.
 *
 * Drei getrennte Schlüssel aus einem Geheimnis: Datei und Vorschaubild sind
 * zwei verschiedene Dateien und dürfen den Nonce-Raum nicht teilen (siehe
 * oben), und die Metadaten werden am Stück verschlüsselt, nicht gerahmt.
 */
export async function teilSchluessel(anhang: Uint8Array, zweck: Zweck): Promise<CryptoKey> {
  const basis = await crypto.subtle.importKey('raw', anhang as BufferSource, 'HKDF', false, ['deriveBits'])
  const bits = await crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new Uint8Array(0),
      info: new TextEncoder().encode(`familyhub/anhang/v1/${zweck}`),
    },
    basis,
    256,
  )
  return crypto.subtle.importKey('raw', bits, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt'])
}

function nonce(index: number): Uint8Array {
  const n = new Uint8Array(12)
  new DataView(n.buffer).setUint32(8, index, false)
  return n
}

function aad(dateiId: Uint8Array, index: number, letzter: boolean): Uint8Array {
  const a = new Uint8Array(DATEI_ID_BYTES + 4 + 1)
  a.set(dateiId, 0)
  new DataView(a.buffer).setUint32(DATEI_ID_BYTES, index, false)
  a[DATEI_ID_BYTES + 4] = letzter ? 1 : 0
  return a
}

/**
 * Verschlüsselt eine Datei rahmenweise.
 *
 * Arbeitet über `Blob.slice` und hält deshalb nie die ganze Datei im Speicher —
 * bei 500 MB Video auf einem Telefon mit 2 GB RAM ist das der Unterschied
 * zwischen „lädt hoch" und „App wird beendet".
 *
 * Eine leere Datei bekommt trotzdem einen Rahmen: Sonst gäbe es keinen
 * letzten, und „abgeschnitten" wäre von „leer" nicht zu unterscheiden.
 */
export async function verschluesseln(
  daten: Blob,
): Promise<{ paket: Blob; schluessel: Uint8Array }> {
  const anhang = neuerAnhangSchluessel()
  const paket = await mitSchluessel(daten, await teilSchluessel(anhang, 'daten'))
  return { paket, schluessel: anhang }
}

/**
 * Verschlüsselt mit einem bereits abgeleiteten Teilschlüssel.
 *
 * Nur für Aufrufer, die selbst dafür sorgen, dass jeder Teilschlüssel genau
 * eine Datei sieht — im Projekt ist das `anhang.ts`, das Datei und Vorschau
 * aus demselben Anhangsschlüssel, aber mit verschiedenem Zweck ableitet.
 */
export async function mitSchluessel(daten: Blob, key: CryptoKey): Promise<Blob> {
  const dateiId = crypto.getRandomValues(new Uint8Array(DATEI_ID_BYTES))

  const kopf = new Uint8Array(KOPF_BYTES)
  kopf.set(MAGIC, 0)
  kopf[MAGIC.length] = VERSION
  kopf.set(dateiId, MAGIC.length + 1)

  const teile: BlobPart[] = [kopf]
  const anzahl = Math.max(1, Math.ceil(daten.size / RAHMEN_BYTES))

  for (let i = 0; i < anzahl; i++) {
    const stueck = await daten.slice(i * RAHMEN_BYTES, (i + 1) * RAHMEN_BYTES).arrayBuffer()
    const letzter = i === anzahl - 1
    const geheim = new Uint8Array(await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: nonce(i) as BufferSource, additionalData: aad(dateiId, i, letzter) as BufferSource },
      key,
      stueck,
    ))
    const laenge = new Uint8Array(LAENGE_BYTES)
    new DataView(laenge.buffer).setUint32(0, geheim.length, false)
    teile.push(laenge, geheim)
  }

  return new Blob(teile)
}

/**
 * Entschlüsselt ein Paket.
 *
 * Gibt erst etwas heraus, wenn **alle** Rahmen geprüft sind. Das ist der
 * Punkt, an dem ADR-011 sich gegen „unauthentifizierte Bytes ausliefern und
 * hinterher prüfen" entschieden hat: Ein halb ausgeliefertes Bild, das sich
 * danach als manipuliert herausstellt, hat der Nutzer bereits gesehen.
 */
export async function entschluesseln(paket: ArrayBuffer, key: CryptoKey): Promise<Blob> {
  const roh = new Uint8Array(paket)
  if (roh.length < KOPF_BYTES) throw new RahmenKaputt('Die Datei ist unvollständig.')
  for (let i = 0; i < MAGIC.length; i++) {
    if (roh[i] !== MAGIC[i]) throw new RahmenKaputt('Das ist keine Family-Hub-Datei.')
  }
  if (roh[MAGIC.length] !== VERSION) {
    throw new RahmenKaputt('Diese Datei wurde mit einer neueren Fassung erstellt.')
  }
  const dateiId = roh.subarray(MAGIC.length + 1, KOPF_BYTES)

  // Erst alle Rahmen abgrenzen, dann entschlüsseln: Die Zahl der Rahmen muss
  // feststehen, bevor der letzte als letzter geprüft werden kann.
  const grenzen: Array<{ von: number; bis: number }> = []
  let p = KOPF_BYTES
  while (p < roh.length) {
    if (p + LAENGE_BYTES > roh.length) throw new RahmenKaputt('Die Datei bricht mitten ab.')
    const laenge = new DataView(roh.buffer, roh.byteOffset + p, LAENGE_BYTES).getUint32(0, false)
    p += LAENGE_BYTES
    if (laenge < TAG_BYTES || p + laenge > roh.length) {
      throw new RahmenKaputt('Die Datei bricht mitten ab.')
    }
    grenzen.push({ von: p, bis: p + laenge })
    p += laenge
  }
  if (grenzen.length === 0) throw new RahmenKaputt('Die Datei enthält keine Daten.')

  const teile: BlobPart[] = []
  for (const [i, g] of grenzen.entries()) {
    const letzter = i === grenzen.length - 1
    try {
      teile.push(await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: nonce(i) as BufferSource, additionalData: aad(dateiId, i, letzter) as BufferSource },
        key,
        roh.subarray(g.von, g.bis),
      ))
    } catch {
      // Eine einzige Meldung für alle Fälle — vertauscht, verändert,
      // abgeschnitten, falscher Schlüssel. Welcher davon zutrifft, ist für
      // den Nutzer dasselbe Ergebnis, und für einen Angreifer wäre die
      // Unterscheidung eine Auskunft.
      throw new RahmenKaputt('Die Datei konnte nicht gelesen werden.')
    }
  }
  return new Blob(teile)
}

/**
 * Dasselbe, aber aus einem Strom — für Dateien, die nicht in den Speicher passen.
 *
 * `entschluesseln` nimmt einen `ArrayBuffer`, und der ist bei einem Video von
 * 200 MB genau das Problem: Er liegt vollständig im JavaScript-Speicher, und
 * die entschlüsselte Kopie daneben. Hier wandert immer nur **ein Rahmen**
 * (64 KiB) durch; die fertigen Stücke gehen in einen `Blob`, den der Browser
 * verwaltet und notfalls auf die Platte auslagert.
 *
 * ## Warum trotzdem erst am Ende etwas herauskommt
 *
 * Ein Rahmen ist geprüft, sobald er entschlüsselt ist — die Datei als Ganzes
 * aber erst, wenn der als „letzter" markierte Rahmen gelesen wurde. Wer vorher
 * ausliefert, liefert Bytes aus einer möglicherweise abgeschnittenen Datei.
 * Für echtes Abspielen während des Ladens bräuchte es wahlfreien Zugriff je
 * Rahmen; ADR-011 führt das als offen, und offen bleibt es.
 */
export async function entschluesselnStrom(
  strom: ReadableStream<Uint8Array>,
  key: CryptoKey,
): Promise<Blob> {
  const leser = strom.getReader()
  let puffer = new Uint8Array(0)
  let fertig = false

  /** Liest so lange nach, bis mindestens `n` Bytes im Puffer stehen. */
  async function mindestens(n: number): Promise<boolean> {
    while (puffer.length < n && !fertig) {
      const { done, value } = await leser.read()
      if (done) { fertig = true; break }
      if (!value || value.length === 0) continue
      const neu = new Uint8Array(puffer.length + value.length)
      neu.set(puffer, 0)
      neu.set(value, puffer.length)
      puffer = neu
    }
    return puffer.length >= n
  }

  function nimm(n: number): Uint8Array {
    const stueck = puffer.subarray(0, n)
    puffer = puffer.subarray(n)
    return stueck
  }

  try {
    if (!await mindestens(KOPF_BYTES)) throw new RahmenKaputt('Die Datei ist unvollständig.')
    const kopf = nimm(KOPF_BYTES)
    for (let i = 0; i < MAGIC.length; i++) {
      if (kopf[i] !== MAGIC[i]) throw new RahmenKaputt('Das ist keine Family-Hub-Datei.')
    }
    if (kopf[MAGIC.length] !== VERSION) {
      throw new RahmenKaputt('Diese Datei wurde mit einer neueren Fassung erstellt.')
    }
    // Kopie: `puffer` wird beim Weiterlesen ersetzt, die Kennung muss bleiben.
    const dateiId = new Uint8Array(kopf.subarray(MAGIC.length + 1, KOPF_BYTES))

    const teile: BlobPart[] = []
    let index = 0
    /*
     * Ein Rahmen wird immer erst dann entschlüsselt, wenn feststeht, ob ein
     * weiterer folgt — denn ob er der letzte ist, steht in seinem AAD. Deshalb
     * wird er zurückgehalten, bis der nächste Längenwert gelesen ist oder der
     * Strom endet.
     */
    let wartend: Uint8Array | null = null

    async function ausgeben(rahmen: Uint8Array, letzter: boolean): Promise<void> {
      try {
        teile.push(await crypto.subtle.decrypt(
          {
            name: 'AES-GCM',
            iv: nonce(index) as BufferSource,
            additionalData: aad(dateiId, index, letzter) as BufferSource,
          },
          key,
          rahmen as BufferSource,
        ))
      } catch {
        throw new RahmenKaputt('Die Datei konnte nicht gelesen werden.')
      }
      index++
    }

    for (;;) {
      const nochWas = await mindestens(LAENGE_BYTES)
      if (!nochWas) {
        if (puffer.length !== 0) throw new RahmenKaputt('Die Datei bricht mitten ab.')
        break
      }
      const laenge = new DataView(
        puffer.buffer, puffer.byteOffset, LAENGE_BYTES).getUint32(0, false)
      nimm(LAENGE_BYTES)
      if (laenge < TAG_BYTES) throw new RahmenKaputt('Die Datei bricht mitten ab.')
      if (!await mindestens(laenge)) throw new RahmenKaputt('Die Datei bricht mitten ab.')
      const rahmen = new Uint8Array(nimm(laenge))

      if (wartend) await ausgeben(wartend, false)
      wartend = rahmen
    }

    if (!wartend) throw new RahmenKaputt('Die Datei enthält keine Daten.')
    await ausgeben(wartend, true)

    return new Blob(teile)
  } finally {
    // Auch bei einem Fehler: Sonst bleibt die Verbindung offen und der Browser
    // lädt im Hintergrund ein Video zu Ende, das niemand mehr sehen will.
    await leser.cancel().catch(() => undefined)
  }
}

/** Metadaten am Stück — sie sind klein, ein Rahmenformat wäre hier Aufwand ohne Zweck. */
export async function metaVerschluesseln(inhalt: unknown, key: CryptoKey): Promise<string> {
  const n = crypto.getRandomValues(new Uint8Array(12))
  const geheim = new Uint8Array(await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: n as BufferSource }, key,
    new TextEncoder().encode(JSON.stringify(inhalt)),
  ))
  const zusammen = new Uint8Array(n.length + geheim.length)
  zusammen.set(n, 0)
  zusammen.set(geheim, n.length)
  let s = ''
  for (const b of zusammen) s += String.fromCharCode(b)
  return btoa(s)
}

export async function metaEntschluesseln<T>(b64: string, key: CryptoKey): Promise<T> {
  const roh = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))
  if (roh.length <= 12) throw new RahmenKaputt('Die Angaben zur Datei fehlen.')
  try {
    const klar = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: roh.subarray(0, 12) as BufferSource }, key, roh.subarray(12))
    return JSON.parse(new TextDecoder().decode(klar)) as T
  } catch {
    throw new RahmenKaputt('Die Angaben zur Datei konnten nicht gelesen werden.')
  }
}
